/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Hook to standardized patterns when applying style logic for actionable elements.
 */

import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { useHover } from '../UNSAFE_useHover';
import { useActive } from '../UNSAFE_useActive';

export type InteractionStyleOptions = {
  isDisabled?: boolean;
  disabledKeys?: ('enter' | 'space')[];
};

const useInteractionStyle = (
  { isDisabled, disabledKeys }: InteractionStyleOptions = { isDisabled: false }
) => {
  const hints = getClientHints();
  // :active only works on IOS devices if a touch handler exists
  const iosProps = hints.platform === 'ios' ? { ontouchstart: function () {} } : {};
  const isHybrid = hints.hoverSupport === 'events';

  // hook props and state
  const { activeProps, isActive } = useActive({ disabledKeys });
  const { hoverProps, isHover } = useHover({ isDisabled: !isHybrid });

  // apply hover styling to element on a device that does not have hoverSupport
  // events and is not active or disabled
  const applyPseudoHoverStyle = !isHybrid && !isActive && !isDisabled;

  // apply hover styling to element on a device that has hoverSupport events
  // and is not active or disabled
  const applyHoverStyle = isHybrid && isHover && !isActive && !isDisabled;

  // apply active style when element is active and not disabled
  const applyActiveStyle = isActive && !isDisabled;

  // merge all props
  const interactionProps = mergeProps(iosProps, activeProps, hoverProps);

  // in the event a component wants to conditionally use base props or hook state,
  // baseProps, and baseStates are returned
  return {
    interactionProps,
    baseProps: {
      iosProps,
      activeProps,
      hoverProps
    },
    applyPseudoHoverStyle,
    applyHoverStyle,
    applyActiveStyle,
    baseStates: {
      isActive,
      isHover
    }
  };
};

export { useInteractionStyle };
